// Analytics and Performance Monitoring Utilities

// Google Analytics 4 Integration
export const initGA = (measurementId) => {
  if (typeof window !== 'undefined' && !window.gtag) {
    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    
    gtag('js', new Date());
    gtag('config', measurementId, {
      page_title: document.title,
      page_location: window.location.href
    });
  }
};

// Track page views
export const trackPageView = (path, title) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_title: title,
      page_location: window.location.href,
      page_path: path
    });
  }
};

// Track custom events
export const trackEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);
  }
};

// Track form submissions
export const trackFormSubmission = (formName, success = true) => {
  trackEvent('form_submit', {
    form_name: formName,
    success: success
  });
};

// Track business events
export const trackQuoteRequest = (productType) => {
  trackEvent('quote_request', {
    product_type: productType,
    value: 1
  });
};

// Performance Monitoring
export const initPerformanceMonitoring = () => {
  if (typeof window !== 'undefined') {
    // Monitor Core Web Vitals
    if ('web-vitals' in window || typeof window.webVitals !== 'undefined') {
      // If web-vitals library is loaded
      const reportWebVitals = (metric) => {
        trackEvent('web_vital', {
          metric_name: metric.name,
          metric_value: metric.value,
          metric_rating: metric.rating
        });
      };

      // You would need to install web-vitals package
      // import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';
      // getCLS(reportWebVitals);
      // getFID(reportWebVitals);
      // getFCP(reportWebVitals);
      // getLCP(reportWebVitals);
      // getTTFB(reportWebVitals);
    }

    // Monitor JavaScript errors
    window.addEventListener('error', (event) => {
      trackEvent('javascript_error', {
        error_message: event.message,
        error_filename: event.filename,
        error_line: event.lineno
      });
    });

    // Monitor unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      trackEvent('unhandled_rejection', {
        error_message: event.reason?.toString() || 'Unknown error'
      });
    });

    // Monitor page load performance
    window.addEventListener('load', () => {
      setTimeout(() => {
        const perfData = performance.getEntriesByType('navigation')[0];
        if (perfData) {
          trackEvent('page_load_performance', {
            load_time: Math.round(perfData.loadEventEnd - perfData.fetchStart),
            dom_content_loaded: Math.round(perfData.domContentLoadedEventEnd - perfData.fetchStart),
            first_paint: Math.round(perfData.responseEnd - perfData.fetchStart)
          });
        }
      }, 0);
    });
  }
};

// Error Reporting (Placeholder for Sentry integration)
export const reportError = (error, errorInfo = {}) => {
  // In production, you would integrate with Sentry or similar service
  console.error('Error reported:', error, errorInfo);
  
  if (typeof window !== 'undefined') {
    trackEvent('application_error', {
      error_name: error.name,
      error_message: error.message,
      error_stack: error.stack?.substring(0, 200), // Truncate for analytics
      component_stack: errorInfo.componentStack?.substring(0, 200)
    });
  }
};

// Business Intelligence Tracking
export const trackUserBehavior = {
  // Track product interest
  productView: (productName, category) => {
    trackEvent('product_view', {
      product_name: productName,
      product_category: category
    });
  },

  // Track navigation patterns
  navigationClick: (section, destination) => {
    trackEvent('navigation_click', {
      section: section,
      destination: destination
    });
  },

  // Track contact attempts
  contactAttempt: (method, location) => {
    trackEvent('contact_attempt', {
      contact_method: method, // 'phone', 'email', 'form', 'whatsapp'
      page_location: location
    });
  },

  // Track quote funnel
  quoteStep: (step, productType) => {
    trackEvent('quote_funnel', {
      step: step, // 'started', 'product_selected', 'form_filled', 'submitted'
      product_type: productType
    });
  }
};

// Initialize analytics (call this in App.js)
export const initAnalytics = () => {
  // Initialize GA4 - Replace with your actual measurement ID
  // initGA('G-XXXXXXXXXX');
  
  // Initialize performance monitoring
  initPerformanceMonitoring();
  
  console.log('Analytics initialized');
};

export default {
  initAnalytics,
  trackPageView,
  trackEvent,
  trackFormSubmission,
  trackQuoteRequest,
  reportError,
  trackUserBehavior
};